"use client";

import { Lock, Package, Play, ShoppingBag, ShoppingCart } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getPlatformShopItems,
  getShopCartCount,
  getShopItemsForHiive,
  type ShopItemForBuyer,
} from "@/actions/shop";
import { PricingDisclosure } from "@/components/pricing/pricing-breakdown";
import { AppImage as Image } from "@/components/ui/app-image";
import { AvailabilityRibbon } from "@/components/ui/availability-ribbon";
import { BackAwareLink } from "@/components/ui/back-aware-link";
import { Skeleton } from "@/components/ui/skeleton";
import { captureClientEvent } from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { ASPECT_RATIO_CLASS } from "@/lib/aspect-ratios";
import { availabilityFor } from "@/lib/availability";
import { isVideoItem, mediaPreviewUrlFromAsset } from "@/lib/media";
import { blurHashToDataUrl } from "@/lib/media/blurhash";
import { cn } from "@/lib/utils";
import { MyOrdersSheet } from "./my-orders-sheet";
import { formatMoney } from "./shop-format";
import { ShopItemSheet } from "./shop-item-sheet";

interface CardShopSectionProps {
  hiiveId: number | null;
  initialItems?: ShopItemForBuyer[];
  initialCartCount?: number;
  hideTitle?: boolean;
  showEmptyState?: boolean;
}

export function CardShopSection({
  hiiveId,
  initialItems,
  initialCartCount,
  hideTitle = false,
  showEmptyState = false,
}: CardShopSectionProps) {
  useEffect(() => {
    captureClientEvent(ANALYTICS_EVENTS.SHOP_VIEWED, { hiive_id: hiiveId });
  }, [hiiveId]);
  const [items, setItems] = useState<ShopItemForBuyer[] | null>(
    initialItems ?? null,
  );
  const [loading, setLoading] = useState(!initialItems);
  const [error, setError] = useState<string | null>(null);
  const [activeItemId, setActiveItemId] = useState<number | null>(null);
  const [ordersOpen, setOrdersOpen] = useState(false);
  const [cartCount, setCartCount] = useState(initialCartCount ?? 0);
  const [activityFilter, setActivityFilter] = useState<string | null>(null);

  useEffect(() => {
    if (initialCartCount !== undefined) return;
    let cancelled = false;
    void getShopCartCount().then((count) => {
      if (!cancelled) setCartCount(count);
    });
    return () => {
      cancelled = true;
    };
  }, [initialCartCount]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    // In-sheet client fetch is safe (not a client-side navigation).
    const result = hiiveId
      ? await getShopItemsForHiive(hiiveId)
      : await getPlatformShopItems();
    if (result.ok) {
      setItems(result.data);
    } else {
      setError(result.message);
    }
    setLoading(false);
  }, [hiiveId]);

  useEffect(() => {
    if (initialItems) {
      setItems(initialItems);
      setLoading(false);
      return;
    }
    void load();
  }, [initialItems, load]);

  const activeItem = items?.find((item) => item.id === activeItemId) ?? null;
  const activityOptions = useMemo(() => {
    const byId = new Map<string, string>();
    for (const item of items ?? []) {
      for (const activity of item.activities)
        byId.set(activity.id, activity.name);
    }
    return Array.from(byId, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [items]);
  const visibleItems = activityFilter
    ? (items ?? []).filter((item) =>
        item.activities.some((activity) => activity.id === activityFilter),
      )
    : items;

  if (!loading && !error && (items?.length ?? 0) === 0) {
    if (!showEmptyState) return null;

    return (
      <p className="rounded-2xl border border-dashed border-border bg-surface/60 px-4 py-5 text-center text-sm text-text-secondary">
        No platform shop items are available right now.
      </p>
    );
  }

  return (
    <>
      <div className="mb-4 flex items-center justify-between gap-2">
        {!hideTitle && (
          <div className="flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-brand" />
            <h2 className="text-lg font-semibold text-text-primary">Shop</h2>
          </div>
        )}
        <div className={cn("flex items-center gap-2", hideTitle && "ml-auto")}>
          <button
            type="button"
            onClick={() => setOrdersOpen(true)}
            className="flex items-center gap-1.5 rounded-full bg-surface-alt px-3 py-1.5 text-xs font-medium text-text-secondary transition-colors hover:bg-surface-elevated hover:text-text-primary"
          >
            <Package className="h-3.5 w-3.5" />
            Orders
          </button>
          <BackAwareLink
            href="/shop/cart"
            aria-label={`Cart${cartCount > 0 ? ` · ${cartCount} item${cartCount === 1 ? "" : "s"}` : ""}`}
            className="relative flex items-center gap-1.5 rounded-full bg-brand/10 px-3 py-1.5 text-xs font-semibold text-brand transition-colors hover:bg-brand/15"
          >
            <ShoppingCart className="h-3.5 w-3.5" />
            Cart
            <AnimatePresence>
              {cartCount > 0 && (
                <motion.span
                  key={cartCount}
                  initial={{ scale: 0.3, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.3, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 520, damping: 17 }}
                  className="flex h-4 min-w-4 items-center justify-center rounded-full bg-brand px-1 text-[10px] leading-none font-bold text-white tabular-nums"
                >
                  {cartCount}
                </motion.span>
              )}
            </AnimatePresence>
          </BackAwareLink>
        </div>
      </div>

      {loading ? (
        <ShopGridSkeleton />
      ) : error ? (
        <div className="rounded-2xl border border-border/60 bg-surface/70 p-4 text-center">
          <p className="text-sm text-text-secondary">{error}</p>
          <button
            type="button"
            onClick={() => void load()}
            className="mt-2 text-sm font-semibold text-brand"
          >
            Try again
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {activityOptions.length > 1 ? (
            <div className="flex gap-2 overflow-x-auto pb-1">
              <button
                type="button"
                onClick={() => setActivityFilter(null)}
                className={cn(
                  "shrink-0 rounded-full border px-3 py-1.5 text-xs font-medium",
                  activityFilter === null
                    ? "border-brand bg-brand/10 text-brand"
                    : "border-border text-text-secondary",
                )}
              >
                All
              </button>
              {activityOptions.map((activity) => (
                <button
                  key={activity.id}
                  type="button"
                  onClick={() => setActivityFilter(activity.id)}
                  className={cn(
                    "shrink-0 rounded-full border px-3 py-1.5 text-xs font-medium",
                    activityFilter === activity.id
                      ? "border-brand bg-brand/10 text-brand"
                      : "border-border text-text-secondary",
                  )}
                >
                  {activity.name}
                </button>
              ))}
            </div>
          ) : null}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {visibleItems?.map((item) => (
              <ShopItemTile
                key={item.id}
                item={item}
                onOpen={() => setActiveItemId(item.id)}
              />
            ))}
          </div>
        </div>
      )}

      <AnimatePresence>
        {activeItem && (
          <ShopItemSheet
            item={activeItem}
            onClose={() => setActiveItemId(null)}
            onAddedToCart={(quantity) =>
              setCartCount((count) => count + quantity)
            }
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {ordersOpen && <MyOrdersSheet onClose={() => setOrdersOpen(false)} />}
      </AnimatePresence>
    </>
  );
}

function ShopItemTile({
  item,
  onOpen,
}: {
  item: ShopItemForBuyer;
  onOpen: () => void;
}) {
  const image = item.images[0];
  const src = mediaPreviewUrlFromAsset(image);
  const isVideo = image ? isVideoItem(image) : false;
  const blur = blurHashToDataUrl(image?.blurHash);
  const isLocked = !item.isEligible;
  const hasCompareAt =
    item.compareAtPriceMinor !== null &&
    item.compareAtPriceMinor > item.priceMinor;

  return (
    // biome-ignore lint/a11y/useSemanticElements: nested content is fine here; role+tabIndex+keydown keep it operable
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}
      className="group flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-border bg-card transition-all hover:shadow-sm"
    >
      <div
        className={cn(
          "relative w-full overflow-hidden bg-surface-alt",
          ASPECT_RATIO_CLASS.portrait4By5,
        )}
      >
        {src ? (
          <Image
            src={src}
            alt={item.name}
            fill
            sizes="(max-width: 640px) 45vw, 200px"
            className={cn(
              "object-cover transition-transform group-hover:scale-[1.02]",
              (isLocked || item.soldOut) && "opacity-60",
            )}
            placeholder={blur ? "blur" : undefined}
            blurDataURL={blur}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-text-tertiary">
            <ShoppingBag className="h-8 w-8" />
          </div>
        )}
        {isVideo && src ? (
          <span className="absolute top-2 right-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur-sm">
            <Play className="ml-0.5 h-3.5 w-3.5 fill-current" />
          </span>
        ) : null}

        {isLocked && (
          <span className="absolute top-2 left-2 flex h-7 w-7 items-center justify-center rounded-full bg-surface-elevated/90 text-text-secondary shadow-sm backdrop-blur-sm">
            <Lock className="h-3.5 w-3.5" />
          </span>
        )}

        <AvailabilityRibbon
          availability={
            item.availability ??
            availabilityFor({
              remaining: item.remainingStock,
              soldOut: item.soldOut,
              unit: "item",
            })
          }
        />
      </div>

      <div className="flex min-w-0 flex-1 flex-col p-3">
        <h3 className="line-clamp-2 text-sm leading-tight font-semibold text-text-primary">
          {item.name}
        </h3>
        {item.activities.length > 0 ? (
          <p className="mt-1 truncate text-[10px] text-brand">
            {item.activities.map((activity) => activity.name).join(" · ")}
          </p>
        ) : null}

        {isLocked ? (
          <div className="mt-2">
            <p className="line-clamp-2 text-[11px] leading-snug text-text-secondary">
              {item.requirementDescription}
            </p>
            <LockedProgressBar item={item} />
          </div>
        ) : (
          <div className="mt-2 flex items-baseline gap-1.5">
            <span className="text-sm font-bold text-text-primary tabular-nums">
              {formatMoney(
                item.advertisedPriceMinor ?? item.priceMinor,
                item.currency,
              )}
              <PricingDisclosure policy={item.pricingPolicy} />
            </span>
            {hasCompareAt && (
              <span className="text-xs text-text-tertiary tabular-nums line-through">
                {formatMoney(item.compareAtPriceMinor as number, item.currency)}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Compact aggregate progress toward eligibility on a locked tile. */
function LockedProgressBar({ item }: { item: ShopItemForBuyer }) {
  const countable = item.requirementProgress.filter(
    (requirement) =>
      typeof requirement.current === "number" &&
      typeof requirement.target === "number" &&
      (requirement.target as number) > 0,
  );
  if (countable.length === 0) return null;
  const ratio =
    countable.reduce((sum, requirement) => {
      const current = requirement.current as number;
      const target = requirement.target as number;
      return sum + Math.min(current / target, 1);
    }, 0) / countable.length;

  return (
    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface-alt">
      <div
        className="h-full rounded-full bg-brand/80 transition-[width] duration-500"
        style={{ width: `${Math.round(ratio * 100)}%` }}
      />
    </div>
  );
}

/** Zero-shift skeleton mirroring the tile: square image + title + price line. */
export function ShopGridSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-3">
      {["a", "b", "c", "d"].map((slot) => (
        <div
          key={slot}
          className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card"
        >
          <Skeleton className={cn("w-full", ASPECT_RATIO_CLASS.portrait4By5)} />
          <div className="flex flex-col gap-2 p-3">
            <Skeleton className="h-3.5 w-3/4 rounded" />
            <Skeleton className="h-3.5 w-1/3 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}
